import { fromJS, Set } from 'immutable'
import * as applicationActions from 'actions/application'
import { createReducer } from './utils'

function createNotification(
  id,
  message,
  type,
  persistent,
  async,
  closable,
  description,
  redirect,
  actionTrigger,
  reportLink,
  format,
  showIcon,
  icon,
  hideOnPaths
) {
  return fromJS({
    id,
    message,
    type,
    persistent,
    async,
    closable,
    description,
    redirect,
    actionTrigger,
    reportLink,
    format,
    showIcon,
    icon,
    hideOnPaths,
  })
}

export const initialState = fromJS({
  header: '',
  headerHover: '',
  backText: '',
  backAction: null,
  notifications: [],
  windows: [],
  modals: [],
  errorMessages: [],
  errorCode: '',
  showModal: false,
  notificationPanelState: true,
  appVersion: process.env.REACT_APP_VERSION,
  toggleMeToForceStateUpdates: true,
  overrideDefaultPageMeta: false,
  asyncSet: Set([]),
  errorDictionary: {},
  selectedAppSection: '',
  hasSelectedAppSection: false,
})

const handlers = {
  [applicationActions.TOGGLE_USELESS_VARIABLE]: toggleUselessVariable,
  [applicationActions.CREATE_SYSTEM_NOTIFICATION]: createSystemNotification,
  [applicationActions.HANDLE_NOTIFICATIONS_PANEL]: handleNotificationPanel,
  [applicationActions.DISPLAY_ERROR_MODAL]: displayErrorModal,
  [applicationActions.HIDE_ERROR_MODAL]: hideErrorModal,
  [applicationActions.DELETE_SYSTEM_NOTIFICATION]: deleteSystemNotification,
  [applicationActions.ASYNC_DISPATCH]: dispatchingAsync,
  [applicationActions.ASYNC_RESOLVE]: asyncResolved,
  [applicationActions.API_ERROR]: asyncError,
  [applicationActions.GET_API_VERSION]: getAPIVersion,
  [applicationActions.SELECTED_APP_SECTION]: getSelectedAppSection,
  [applicationActions.HAS_SELECTED_APP_SECTION]: getHasSelectedAppSection,
}

export default createReducer(initialState, handlers)

// TODO: This action exists because for some reason dispatching goBack does not update the page state correctly until you call a second action. We
// dispatch this after every goBack call to make the page load correctly.
function toggleUselessVariable(state) {
  return state.set(
    'toggleMeToForceStateUpdates',
    !state.get('toggleMeToForceStateUpdates')
  )
}

function getAPIVersion(state, { data = '' }) {
  return state.set('appVersion', `${data} ${process.env.REACT_APP_VERSION}`)
}

function getSelectedAppSection(state, { selectedSection }) {
  return state.set('selectedAppSection', selectedSection)
}

function getHasSelectedAppSection(state, { isSelected }) {
  return state.set('hasSelectedAppSection', isSelected)
}

function handleNotificationPanel(state, { isExpanded }) {
  return state.set('notificationPanelState', isExpanded)
}

function createSystemNotification(
  state,
  {
    notificationId,
    notificationMessage,
    notificationType,
    notificationFormat,
    persistent,
    async,
    closable,
    description,
    redirect,
    actionTrigger,
    reportLink,
    showIcon,
    icon,
    hideOnPaths,
  }
) {
  const notification = createNotification(
    notificationId,
    notificationMessage,
    notificationType,
    persistent,
    async,
    closable,
    description,
    redirect,
    actionTrigger,
    reportLink,
    notificationFormat,
    showIcon,
    icon,
    hideOnPaths
  )
  const notifications = state.get('notifications')

  // Don't create duplicates of notifications.
  if (
    notifications.find(
      (notificationObj) => notificationObj.get('id') === notificationId
    )
  ) {
    return state
  }
  // update expand panel to true if
  return state.updateIn(['notifications'], (notificationsArray) =>
    notificationsArray.push(notification)
  )
}

function deleteSystemNotification(state, { id }) {
  const notifications = state
    .get('notifications')
    .filter((n) => n.get('id') !== id)
  return state.set('notifications', notifications)
}

function displayErrorModal(state, { errorMessages, errorCode }) {
  return state.merge({
    errorMessages,
    errorCode,
    showModal: true,
  })
}

function hideErrorModal(state) {
  return state.merge({
    errorMessages: [],
    errorCode: '',
    showModal: false,
  })
}

function dispatchingAsync(state, { signal }) {
  const queue = state.get('asyncSet')
  if (queue.has(signal)) return state

  return state.set('asyncSet', queue.add(signal))
}

function asyncResolved(state, { signal }) {
  const queue = state.get('asyncSet')
  if (!queue.has(signal)) return state

  return state.set('asyncSet', queue.delete(signal))
}

function asyncError(state, { signal, ...err }) {
  return state.setIn(['errorDictionary', signal], err)
}
